import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import UniversalLayout from "./components/UniversalLayout";
import "./LandingPage.css";
import "./ExamplesPage.css";

/* ─────────────────────────────────────────────
   Placeholder videos — reuse existing CDN assets
   ───────────────────────────────────────────── */

const PLACEHOLDER_VIDEOS = {
  portrait: [
    "https://latted.b-cdn.net/JC-innout.mp4",
    "https://latted.b-cdn.net/misc/latted%20launch%20video%20viral%20final.mp4",
    "https://latted.b-cdn.net/misc/Untitled_Project.mp4",
  ],
  landscape: [
    "https://latted.b-cdn.net/Shot%20Documentary.mp4",
    "https://latted.b-cdn.net/misc/trailer%20with%20thumbnail.mp4",
    "https://latted.b-cdn.net/misc/tbpn_2025_rewind.mp4",
  ],
};

const pickVideo = (aspect, i) => {
  const pool = aspect === "9:16" ? PLACEHOLDER_VIDEOS.portrait : PLACEHOLDER_VIDEOS.landscape;
  return pool[i % pool.length];
};

const logo = (name) => `/brand-logos/${name}`;

// Per-logo scale compensation. Source PNGs have been trimmed to their alpha
// bounding box, so most logos normalize cleanly at scale 1 inside the fixed
// logo slot. Nudge an entry here (~0.85 – 1.2) if a specific mark still
// reads too heavy or too light next to its peers.
const LOGO_SCALE = {};

const logoScaleFor = (path) => {
  const name = path?.split("/").pop();
  return LOGO_SCALE[name] ?? 1;
};

/* ─────────────────────────────────────────────
   Category data
   ───────────────────────────────────────────── */

const sample = (name) => `https://latted.b-cdn.net/examples/${name}`;

const CATEGORIES = [
  {
    id: "fashion",
    label: "Fashion & Apparel",
    description: "Clothing, shoes, and accessories.",
    items: [
      {
        id: "f1",
        aspect: "9:16",
        logo: logo("alo.png"),
        logoAlt: "Alo",
        videoUrl: sample("alo.mp4"),
      },
      {
        id: "f2",
        aspect: "9:16",
        logo: logo("skims.png"),
        logoAlt: "Skims",
        videoUrl: sample("skims.mp4"),
      },
      {
        id: "f3",
        aspect: "9:16",
        logo: logo("gymshark.png"),
        logoAlt: "Gymshark",
        videoUrl: sample("gymshark.mp4"),
      },
    ],
  },
  {
    id: "health",
    label: "Health & Wellness",
    description: "Fitness, nutrition, cosmetic, and wellness products.",
    items: [
      {
        id: "h1",
        aspect: "9:16",
        logo: logo("ag1.png"),
        logoAlt: "AG1",
        videoUrl: sample("ag1.mp4"),
      },
      {
        id: "h2",
        aspect: "9:16",
        logo: logo("huel.png"),
        logoAlt: "Huel",
        videoUrl: sample("huel.mp4"),
      },
      {
        id: "h3",
        aspect: "9:16",
        logo: logo("tabs.png"),
        logoAlt: "Tabs Chocolate",
        videoUrl: sample("tabs.mp4"),
      },
      {
        id: "h4",
        aspect: "9:16",
        logo: logo("ritual.png"),
        logoAlt: "Ritual",
        videoUrl: sample("ritual.mp4"),
      },
    ],
  },
  {
    id: "apps",
    label: "Mobile Apps",
    description: "Mobile apps and games.",
    items: [
      {
        id: "a1",
        aspect: "9:16",
        logo: logo("kalshi.png"),
        logoAlt: "Kalshi",
        videoUrl: sample("kalshi.mp4"),
      },
      {
        id: "a2",
        aspect: "9:16",
        logo: logo("cal-ai.png"),
        logoAlt: "Cal AI",
        videoUrl: sample("cal-ai-1.mp4"),
      },
      {
        id: "a3",
        aspect: "9:16",
        logo: logo("candle.png"),
        logoAlt: "Candle",
        videoUrl: sample("candle.mp4"),
      },
      {
        id: "a4",
        aspect: "9:16",
        logo: logo("cal-ai.png"),
        logoAlt: "Cal AI",
        videoUrl: sample("cal-ai-2.mp4"),
      },
    ],
  },
  {
    id: "web",
    label: "Web",
    description: "B2C or B2B SaaS websites.",
    items: [
      {
        id: "w1",
        aspect: "9:16",
        logo: logo("perplexity.png"),
        logoAlt: "Perplexity",
        videoUrl: sample("perplexity.mp4"),
      },
    ],
  },
  {
    id: "entertainment",
    label: "Entertainment",
    description: "Trailers, skits, and viral shorts.",
    items: [
      {
        id: "e1",
        aspect: "16:9",
        title: "John Cena at InNOut",
        videoUrl: "https://latted.b-cdn.net/JC-innout.mp4",
      },
      {
        id: "e2",
        aspect: "16:9",
        title: "Feature Film Beginning",
        videoUrl: "https://latted.b-cdn.net/misc/Untitled_Project.mp4",
      },
      {
        id: "e3",
        aspect: "16:9",
        title: "TBPN 2025 Rewind",
        videoUrl: "https://latted.b-cdn.net/misc/tbpn_2025_rewind.mp4",
      },
      {
        id: "e4",
        aspect: "16:9",
        title: "YouTube Documentary",
        videoUrl: "https://latted.b-cdn.net/Shot%20Documentary.mp4",
      },
      {
        id: "e5",
        aspect: "16:9",
        title: "OpenAI Movie Trailer",
        videoUrl: "https://latted.b-cdn.net/misc/trailer%20with%20thumbnail.mp4",
      },
      {
        id: "e6",
        aspect: "16:9",
        title: "Latted Launch Video",
        videoUrl: "https://latted.b-cdn.net/misc/latted%20launch%20video%20viral%20final.mp4",
      },
    ],
  },
];

/* ─────────────────────────────────────────────
   Video card
   ───────────────────────────────────────────── */

// Hover previews are skipped for reduced motion, touch-only devices, and
// constrained networks so cards never autoplay motion or data unexpectedly.
function canHoverPreview() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (window.matchMedia("(hover: none)").matches) return false;
  const connection = navigator.connection;
  if (connection?.saveData) return false;
  if (connection?.effectiveType && /2g/.test(connection.effectiveType)) return false;
  return true;
}

export function ExampleVideoCard({ item, videoUrl, onOpen }) {
  const videoRef = useRef(null);
  const rootRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [thumbnailSrc, setThumbnailSrc] = useState(null);

  // Pause and reset the preview when the card scrolls out of view so only
  // visible cards ever play.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setIsHovered(false);
          if (videoRef.current) {
            videoRef.current.pause();
            try {
              videoRef.current.currentTime = 0;
            } catch {
              // ignore seek failures
            }
          }
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Capture a frame for the poster so the card doesn't flash black before hover.
  useEffect(() => {
    let cancelled = false;
    const el = videoRef.current;
    if (!el) return;

    const capture = async () => {
      try {
        if (el.readyState < 1) {
          await new Promise((resolve, reject) => {
            const ok = () => {
              cleanup();
              resolve();
            };
            const err = () => {
              cleanup();
              reject(new Error("metadata"));
            };
            const cleanup = () => {
              el.removeEventListener("loadedmetadata", ok);
              el.removeEventListener("error", err);
            };
            el.addEventListener("loadedmetadata", ok, { once: true });
            el.addEventListener("error", err, { once: true });
          });
        }
        const target = Math.min(0.1, (el.duration || 0) / 2);
        await new Promise((resolve, reject) => {
          const ok = () => {
            cleanup();
            resolve();
          };
          const err = () => {
            cleanup();
            reject(new Error("seek"));
          };
          const cleanup = () => {
            el.removeEventListener("seeked", ok);
            el.removeEventListener("error", err);
          };
          el.addEventListener("seeked", ok, { once: true });
          el.addEventListener("error", err, { once: true });
          try {
            el.currentTime = target;
          } catch (e) {
            cleanup();
            reject(e);
          }
        });
        const w = el.videoWidth;
        const h = el.videoHeight;
        if (!w || !h) return;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(el, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        if (!cancelled) setThumbnailSrc(dataUrl);
        try {
          el.currentTime = 0;
        } catch {
          /* ignore */
        }
      } catch {
        /* CORS or network — fall back to video poster */
      }
    };
    capture();
    return () => {
      cancelled = true;
    };
  }, [videoUrl]);

  const handleEnter = () => {
    if (!canHoverPreview()) return;
    setIsHovered(true);
    videoRef.current?.play().catch(() => {});
  };
  const handleLeave = () => {
    setIsHovered(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <button
      ref={rootRef}
      type="button"
      className={`ex-card ex-card--${item.aspect === "9:16" ? "portrait" : "landscape"}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={() => onOpen({ ...item, videoUrl })}
      aria-label={item.title || item.logoAlt || "Example video"}
    >
      <div className="ex-card-media">
        {thumbnailSrc && (
          <img
            src={thumbnailSrc}
            alt=""
            className={`ex-card-thumb ${isHovered ? "ex-card-thumb--hidden" : ""}`}
          />
        )}
        <video
          ref={videoRef}
          src={videoUrl}
          className={`ex-card-video ${isHovered ? "ex-card-video--visible" : ""}`}
          crossOrigin="anonymous"
          muted
          loop
          playsInline
          preload="metadata"
        />
      </div>

      <div className="ex-card-overlay">
        <div className="ex-card-brand">
          {item.logo ? (
            <span className="ex-card-logo-slot">
              <img
                src={item.logo}
                alt={item.logoAlt}
                className="ex-card-logo"
                style={{ "--logo-scale": logoScaleFor(item.logo) }}
              />
            </span>
          ) : (
            <span className="ex-card-title">{item.title}</span>
          )}
        </div>
      </div>

      <span className="ex-card-expand" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 3 21 3 21 9" />
          <polyline points="9 21 3 21 3 15" />
          <line x1="21" y1="3" x2="14" y2="10" />
          <line x1="3" y1="21" x2="10" y2="14" />
        </svg>
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────
   Row with left/right scroll affordances
   ───────────────────────────────────────────── */

function CategoryRow({ category, onOpen }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section className="ex-category" id={category.id}>
      <div className="ex-category-head">
        <div>
          <h2 className="ex-category-title">{category.label}</h2>
          <p className="ex-category-desc">{category.description}</p>
        </div>
        <div className="ex-category-nav">
          <button
            type="button"
            className="ex-nav-btn"
            onClick={() => scrollBy(-1)}
            disabled={!canLeft}
            aria-label={`Scroll ${category.label} left`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className="ex-nav-btn"
            onClick={() => scrollBy(1)}
            disabled={!canRight}
            aria-label={`Scroll ${category.label} right`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      <div
        className={`ex-scroller ${canLeft ? "ex-scroller--fade-l" : ""} ${canRight ? "ex-scroller--fade-r" : ""}`}
      >
        <div className="ex-scroller-track" ref={scrollerRef}>
          {category.items.map((item, idx) => (
            <ExampleVideoCard
              key={item.id}
              item={item}
              videoUrl={item.videoUrl ?? pickVideo(item.aspect, idx)}
              onOpen={onOpen}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   Expanded video modal
   ───────────────────────────────────────────── */

export function ExampleModal({ item, onClose }) {
  const backdropRef = useRef(null);
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const dialogLabel = item.title || item.logoAlt || "Example video";

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const container = containerRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll(
          'button, video, a[href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const closeButton = containerRef.current?.querySelector(".video-modal-close");
    closeButton?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function") {
        previous.focus();
      }
    };
  }, [onClose]);

  return (
    <div
      ref={backdropRef}
      className="video-modal-backdrop"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}
        className={`video-modal-container ex-modal-container ex-modal-container--${
          item.aspect === "9:16" ? "portrait" : "landscape"
        }`}
      >
        <button className="video-modal-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <div className="video-modal-content ex-modal-content">
          <video
            src={item.videoUrl}
            controls
            autoPlay
            tabIndex={0}
            className={`ex-modal-player ex-modal-player--${
              item.aspect === "9:16" ? "portrait" : "landscape"
            }`}
            crossOrigin="anonymous"
          />
          <div className="ex-modal-caption">
            {item.logo ? (
              <span className="ex-modal-logo-slot">
                <img
                  src={item.logo}
                  alt={item.logoAlt}
                  className="ex-modal-logo"
                  style={{ "--logo-scale": logoScaleFor(item.logo) }}
                />
              </span>
            ) : (
              <span className="ex-modal-title">{item.title}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Page
   ───────────────────────────────────────────── */

export default function ExamplesPage() {
  const [active, setActive] = useState(null);
  const handleOpen = useCallback((item) => setActive(item), []);
  const handleClose = useCallback(() => setActive(null), []);

  return (
    <UniversalLayout>
      <div className="landing-content ex-page">
        <header className="ex-hero">
          <h1 className="ex-hero-title">
            100% AI. Made with <span className="ex-hero-title-em">Latted</span>
          </h1>
          <p className="ex-hero-subtitle">
            All of these videos were created by an AI agent. No human editing. No real footage.
          </p>
          <div className="ex-hero-cta">
            <Link to="/" className="ex-hero-cta-btn">Try for free</Link>
          </div>
        </header>

        <div className="ex-categories">
          {CATEGORIES.map((c) => (
            <CategoryRow key={c.id} category={c} onOpen={handleOpen} />
          ))}
        </div>

        {active && <ExampleModal item={active} onClose={handleClose} />}
      </div>
    </UniversalLayout>
  );
}
